import { Injectable } from '@angular/core';

export interface PhoneConfig {
	maxDigits: number | null;
	format: boolean;
}

export interface ParsedPhone {
	prefix: string;
	number: string;
}

/**
 * Centralized service for phone number formatting and parsing.
 * Supports Spain (+34) and Andorra (+376) with specific formatting rules.
 */
@Injectable({
	providedIn: 'root',
})
export class PhoneService {
	private readonly FLAG_URLS: Record<string, string> = {
		'+34': 'assets/layout/images/esp.png',
		'+376': 'assets/layout/images/and.png',
	};

	/**
	 * Get phone configuration based on prefix
	 */
	getPhoneConfig(prefix: string): PhoneConfig {
		if (prefix === '+34') {
			return { maxDigits: 9, format: true };
		} else if (prefix === '+376') {
			return { maxDigits: 6, format: true };
		}
		return { maxDigits: null, format: false };
	}

	/**
	 * Parse a phone string into prefix and number components
	 * Supports formats:
	 * - New format: "+34|612345678"
	 * - Legacy: "+34612345678" or "+376123456"
	 * - Object: { prefix: string, number: string }
	 */
	parsePhone(phone: string | { prefix: string; number: string } | null | undefined): ParsedPhone {
		if (!phone) {
			return { prefix: '+34', number: '' };
		}

		// If phone is already an object with prefix and number
		if (typeof phone === 'object' && phone.prefix !== undefined && phone.number !== undefined) {
			return { prefix: phone.prefix || '+34', number: phone.number || '' };
		}

		// Ensure phone is a string
		if (typeof phone !== 'string') {
			return { prefix: '+34', number: '' };
		}

		// New format with separator: "+34|612345678"
		if (phone.includes('|')) {
			const [prefix, number] = phone.split('|');
			return { prefix: prefix || '+34', number: (number || '').replace(/\s/g, '') };
		}

		// Legacy format: Known prefixes
		if (phone.startsWith('+34')) {
			return { prefix: '+34', number: phone.slice(3).replace(/\s/g, '') };
		}
		if (phone.startsWith('+376')) {
			return { prefix: '+376', number: phone.slice(4).replace(/\s/g, '') };
		}

		// Unknown format: return as-is
		return { prefix: '', number: phone };
	}

	/**
	 * Format a phone number based on country prefix
	 * - Spain (+34): XXX XXX XXX (9 digits)
	 * - Andorra (+376): XXX XXX (6 digits)
	 * - Others: no formatting
	 */
	formatNumber(number: string, prefix: string): string {
		// Remove any non-digits
		const cleanNumber = number.replace(/\D/g, '');

		if (!cleanNumber) return '';

		const config = this.getPhoneConfig(prefix);

		if (!config.format) {
			return cleanNumber;
		}

		// Limit digits
		const limited = config.maxDigits ? cleanNumber.slice(0, config.maxDigits) : cleanNumber;

		// Format with spaces every 3 digits
		if (prefix === '+34') {
			// Spain: XXX XXX XXX
			if (limited.length > 6) {
				return `${limited.slice(0, 3)} ${limited.slice(3, 6)} ${limited.slice(6)}`;
			} else if (limited.length > 3) {
				return `${limited.slice(0, 3)} ${limited.slice(3)}`;
			}
			return limited;
		} else if (prefix === '+376') {
			// Andorra: XXX XXX
			if (limited.length > 3) {
				return `${limited.slice(0, 3)} ${limited.slice(3)}`;
			}
			return limited;
		}

		return limited;
	}

	/**
	 * Format a complete phone value for display
	 * Input can be string "+34|612345678" or object { prefix, number }
	 * Returns formatted string like "+34 612 345 678"
	 */
	formatForDisplay(value: string | { prefix: string; number: string } | null | undefined): string {
		if (!value) return '';

		const parsed = this.parsePhone(value);

		if (!parsed.prefix && !parsed.number) return '';

		// For unknown prefixes, return as-is
		if (!parsed.prefix) {
			return parsed.number;
		}

		const formattedNumber = this.formatNumber(parsed.number, parsed.prefix);
		return `${parsed.prefix} ${formattedNumber}`.trim();
	}

	/**
	 * Same as `formatForDisplay` but omits the `+34` prefix when present.
	 * The product is Spain-only, so showing the Spanish prefix on every
	 * row is noise. Non-Spanish prefixes (e.g. +376) are still rendered
	 * so foreign numbers remain unambiguous. Used by the table column
	 * `type='phone'`; the rich inputs/components keep `formatForDisplay`
	 * because they show the country chip explicitly.
	 */
	formatForDisplayLocal(value: string | { prefix: string; number: string } | null | undefined): string {
		if (!value) return '';
		const parsed = this.parsePhone(value);
		if (!parsed.prefix && !parsed.number) return '';
		if (!parsed.prefix) {
			return parsed.number;
		}
		const formattedNumber = this.formatNumber(parsed.number, parsed.prefix);
		if (parsed.prefix === '+34') {
			return formattedNumber;
		}
		return `${parsed.prefix} ${formattedNumber}`.trim();
	}

	/**
	 * Get flag URL for a phone prefix
	 * Returns null for unsupported prefixes
	 */
	getFlagUrl(value: string | { prefix: string; number: string } | null | undefined): string | null {
		if (!value) return null;

		let prefix: string;

		if (typeof value === 'object') {
			prefix = value.prefix || '';
		} else {
			// Extract prefix from string
			if (value.includes('|')) {
				prefix = value.split('|')[0];
			} else if (value.startsWith('+34')) {
				prefix = '+34';
			} else if (value.startsWith('+376')) {
				prefix = '+376';
			} else {
				return null;
			}
		}

		return this.FLAG_URLS[prefix] || null;
	}

	/**
	 * Format a phone code/prefix (ensures it starts with +)
	 */
	formatPhoneCode(value: string): string {
		const numbers = value.replace(/\D/g, '');
		return numbers ? '+' + numbers : '';
	}

	/**
	 * Check if a prefix is a known/supported prefix
	 */
	isKnownPrefix(prefix: string): boolean {
		return prefix === '+34' || prefix === '+376';
	}
}
